/**
 * Rate Change Preview Modal
 * Displays a unified comparison table showing current vs proposed schedule
 * with impacted rows highlighted
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CheckCircle as CheckCircleIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { apiPost } from '../../utils/apiClient';
import logger from '../../utils/logger';
import { formatNumber, formatShortDate as formatDate } from '../../utils/formatters';
import useCurrencyFormatter from '../../hooks/useCurrencyFormatter';
import ImpactSummaryCards from './ImpactSummaryCards';
import ScheduleComparisonTable from './ScheduleComparisonTable';
import {
  buildRealMethodCalcDetail as buildRealMethodCalcDetailShared,
  narrationClass,
  getFlowType,
  isInterestPaymentNarration,
  isInterestNarration,
  isPeriodAccrual,
} from '../../utils/scheduleComparison';

function RateChangePreviewModal({
  open,
  onClose,
  rateChangeId,
  onApprove,
  currencyCode = 'ZAR',
  // Pre-fetched preview payload. When supplied, the modal uses it directly and
  // skips the internal fetch — used by the adhoc submit-time preview where no
  // contract_rate_changes row exists yet to load by id.
  previewData: previewDataProp,
  // Hide the "Approve Rate Change" CTA. Used in the same adhoc submit-time
  // context — the row hasn't been created yet, so there's nothing to approve.
  showApprove = true,
  // When true, render a slim LinearProgress strip at the top of the dialog
  // while a background refresh (e.g. adhoc form blur auto-refresh) is in
  // flight. The existing previewData stays rendered underneath so the user
  // doesn't lose context.
  refreshing = false,
}) {
  const { formatCurrency, dp } = useCurrencyFormatter(currencyCode);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [ackReversal, setAckReversal] = useState(false);

  useEffect(() => {
    if (!open) {
      setAckReversal(false);
      return;
    }
    // Pre-fetched mode: caller passed previewData directly. Adopt it and skip
    // the network fetch.
    if (previewDataProp) {
      setPreviewData(previewDataProp);
      setLoading(false);
      setError(null);
      setAckReversal(false);
      return;
    }
    if (rateChangeId) {
      fetchPreview();
      setAckReversal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchPreview is defined below and runs on open/rateChangeId/previewDataProp change
  }, [open, rateChangeId, previewDataProp]);

  const fetchPreview = async () => {
    setLoading(true);
    setError(null);
    setPreviewData(null);
    try {
      const response = await apiPost(`/rate-changes/${rateChangeId}/preview`);

      if (!response?.success || !response?.data) {
        throw new Error(response?.message || 'Invalid response from server');
      }

      const data = response.data;
      if (!data.rate_change || !data.preview_schedule) {
        throw new Error('Preview data is incomplete');
      }

      setPreviewData(data);
    } catch (err) {
      logger.error('Preview fetch error', { message: err.message });
      setError(err.message || 'Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    try {
      await onApprove();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to approve rate change');
    } finally {
      setApproving(false);
    }
  };


  const formatDifference = (diff, isPercentage = false) => {
    if (!diff && diff !== 0) return '-';
    const num = parseFloat(diff);
    const sign = num >= 0 ? '+' : '';
    if (isPercentage) {
      return `${sign}${num.toFixed(2)}%`;
    }
    return `${sign}${currencyCode} ${formatNumber(Math.abs(num), 2)}`;
  };

  /**
   * Pre-process schedule to inject synthetic monthly Interest Accrual entries
   * for any Interest Payment that has no preceding accruals in its period.
   * This handles older contracts where only payment rows exist in the DB.
   */
  const injectSyntheticAccruals = (schedule, dayCountBasis = 365) => {
    const msPerDay = 86400000;

    /**
     * Helper: split a multi-month period into monthly sub-entries.
     * Steps from periodStart to periodEnd using same-day-of-month boundaries
     * (the canonical addMonths pattern used by the amortization engine).
     */
    const splitIntoMonthlyEntries = (periodStartObj, periodEndObj, entry, preserveNarration) => {
      const principal = parseFloat(entry.outstanding_balance || 0);
      const rate = parseFloat(entry.interest_rate || 0);
      const grossTotal = parseFloat(entry.gross_interest_amount || entry.interest_amount || 0);
      const subEntries = [];
      let accumulated = 0;
      let accrualStart = new Date(periodStartObj);

      while (accrualStart < periodEndObj) {
        let monthEnd = new Date(Date.UTC(
          accrualStart.getUTCFullYear(),
          accrualStart.getUTCMonth() + 1,
          accrualStart.getUTCDate()
        ));
        if (monthEnd > periodEndObj) {
          monthEnd = new Date(periodEndObj);
        }

        const daysInPeriod = Math.round((monthEnd - accrualStart) / msPerDay);
        if (daysInPeriod > 0) {
          const grossInterest = principal * (rate / 100) * (daysInPeriod / dayCountBasis);
          accumulated += grossInterest;
          const isLast = monthEnd.getTime() === periodEndObj.getTime();

          subEntries.push({
            payment_date: monthEnd.toISOString().substring(0, 10),
            narration: (isLast && preserveNarration) ? (entry.narration || 'Interest Accrual') : 'Interest Accrual',
            days_in_period: daysInPeriod,
            outstanding_balance: principal,
            interest_rate: rate,
            gross_interest_amount: grossInterest,
            interest_amount: grossInterest,
            net_interest_amount: grossInterest,
            capital_amount: 0,
            payment_amount: 0,
            wht_rate_applied: entry.wht_rate_applied,
            wht_gross_up: entry.wht_gross_up,
          });
        }

        accrualStart = new Date(monthEnd);
      }

      // Scale so sub-entries sum to the original total
      if (accumulated > 0 && grossTotal > 0) {
        const scaleFactor = grossTotal / accumulated;
        subEntries.forEach(sa => {
          sa.gross_interest_amount *= scaleFactor;
          sa.interest_amount = sa.gross_interest_amount;
          sa.net_interest_amount = sa.gross_interest_amount;
        });
      }

      return subEntries;
    };

    const enriched = [];
    let lastPaymentDateStr = null;

    for (let i = 0; i < schedule.length; i++) {
      const entry = schedule[i];
      const narration = entry.narration || '';

      // 1) Handle multi-month Interest Accrual entries (e.g., old lump "Pre-Rate Change" entries).
      // isPeriodAccrual excludes 'Interest Accrual Adjustment' (a manual carry-over, not a period
      // accrual) so the rename stays behaviour-neutral — same as pre-rename 'Carried Interest Accrual'.
      if (isPeriodAccrual(narration) && parseInt(entry.days_in_period) > 35) {
        const entryDateStr = String(entry.payment_date || '').substring(0, 10);
        const entryDateObj = new Date(entryDateStr + 'T00:00:00Z');
        const days = parseInt(entry.days_in_period);

        // The entry's period start based on its days_in_period
        const rawPeriodStart = new Date(entryDateObj);
        rawPeriodStart.setUTCDate(rawPeriodStart.getUTCDate() - days);

        // Check if earlier entries already cover part of this period.
        // Find the last accrual or payment date already in enriched — that's the true period start.
        let truePeriodStart = rawPeriodStart;
        for (let j = enriched.length - 1; j >= 0; j--) {
          const prevDate = String(enriched[j].payment_date || '').substring(0, 10);
          if (prevDate && prevDate > rawPeriodStart.toISOString().substring(0, 10)) {
            truePeriodStart = new Date(prevDate + 'T00:00:00Z');
            break;
          }
        }

        const correctedDays = Math.round((entryDateObj - truePeriodStart) / msPerDay);

        if (correctedDays <= 35) {
          // Period is now single-month after trimming overlap — just fix days_in_period
          const principal = parseFloat(entry.outstanding_balance || 0);
          const rate = parseFloat(entry.interest_rate || 0);
          const correctedInterest = principal * (rate / 100) * (correctedDays / dayCountBasis);
          enriched.push({
            ...entry,
            days_in_period: correctedDays,
            gross_interest_amount: correctedInterest,
            interest_amount: correctedInterest,
            net_interest_amount: correctedInterest,
          });
        } else {
          // Still multi-month — split into monthly sub-entries from true start
          const subEntries = splitIntoMonthlyEntries(truePeriodStart, entryDateObj, entry, true);
          if (subEntries.length > 0) {
            enriched.push(...subEntries);
          } else {
            enriched.push(entry);
          }
        }
        continue;
      }

      // 2) For non-payment entries, pass through as-is. Includes Combined Payment
      // (interest + capital folded) so it triggers synthetic-accrual injection and
      // advances the period cursor like a regular interest payment.
      if (!isInterestPaymentNarration(narration)) {
        enriched.push(entry);
        continue;
      }

      // 3) Interest Payment — inject synthetic accruals if none exist for this period
      const paymentDateStr = String(entry.payment_date || '').substring(0, 10);

      const hasAccruals = enriched.some(e => {
        const n = e.narration || '';
        const d = String(e.payment_date || '').substring(0, 10);
        return isPeriodAccrual(n)
          && (!lastPaymentDateStr || d > lastPaymentDateStr)
          && d <= paymentDateStr;
      });

      if (!hasAccruals) {
        const principal = parseFloat(entry.outstanding_balance || 0);
        const rate = parseFloat(entry.interest_rate || 0);

        let periodStartStr = lastPaymentDateStr;
        if (!periodStartStr && schedule.length > 0) {
          periodStartStr = String(schedule[0].payment_date || '').substring(0, 10);
        }

        if (periodStartStr && principal > 0 && rate > 0) {
          const periodStartObj = new Date(periodStartStr + 'T00:00:00Z');
          const paymentDateObj = new Date(paymentDateStr + 'T00:00:00Z');

          const subEntries = splitIntoMonthlyEntries(periodStartObj, paymentDateObj, entry, false);
          enriched.push(...subEntries);
        }
      }

      enriched.push(entry);
      lastPaymentDateStr = paymentDateStr;
    }

    return enriched;
  };

  // Positional adapter over the shared (named-arg) calc-detail builder, so the
  // split-aware buildDisplayRows call sites stay terse. Single source of truth
  // for the formula lives in utils/scheduleComparison.
  const buildRealMethodCalcDetail = (principal, rate, days, dayCountBasis, calcType, compoundingFrequency, calculatedValue, opts = {}) =>
    buildRealMethodCalcDetailShared({
      principal, rate, days, dayCountBasis, calcType, compoundingFrequency, calculatedValue,
      dp, mode: opts.mode || 'current', auditDailyRate: opts.auditDailyRate,
    });

  /**
   * Build display rows using the current schedule as the base.
   * For interest entries at/after the effective date, calculate the proposed interest
   * directly using the entry's days_in_period and outstanding_balance with the new rate.
   * This avoids any matching issues between current and preview schedules.
   *
   * buildRealMethodCalcDetail / narrationClass / getFlowType / isInterest* come
   * from utils/scheduleComparison so the calc + classification logic is identical
   * to the amendment/regen previews (financial-system consistency).
   */
  // Index the backend preview_schedule (real-method values) so the proposed
  // column is read from what the contract will ACTUALLY book on approval, rather
  // than re-derived with flat simple interest. Keyed by `${date}|${class}`.
  // Post-effective accruals can repeat a date across a split, so each key holds a
  // FIFO queue consumed in document order.
  const buildProposedIndex = (previewSchedule, effectiveDateStr) => {
    const index = new Map();
    (previewSchedule || []).forEach(e => {
      const dateStr = String(e.payment_date || '').substring(0, 10);
      if (!effectiveDateStr || dateStr < effectiveDateStr) return; // only proposed side matters
      const cls = narrationClass(e.narration);
      if (cls === 'other') return;
      const key = `${dateStr}|${cls}`;
      if (!index.has(key)) index.set(key, []);
      index.get(key).push(e);
    });
    return index;
  };

  const takeProposed = (index, dateStr, cls) => {
    const key = `${dateStr}|${cls}`;
    const queue = index.get(key);
    if (queue && queue.length > 0) return queue.shift();
    return null;
  };

  const buildDisplayRows = (currentSchedule, effectiveDate, oldRate, newRate, dayCountBasis = 365, calcType = 'Simple', compoundingFrequency = 12, previewSchedule = []) => {
    const current = currentSchedule || [];
    const enrichedSchedule = injectSyntheticAccruals(current, dayCountBasis);
    const rows = [];
    const effectiveDateStr = effectiveDate ? String(effectiveDate).substring(0, 10) : '';
    const effectiveDateObj = effectiveDateStr ? new Date(effectiveDateStr + 'T00:00:00Z') : null;
    const newRateNum = parseFloat(newRate || 0);

    // Proposed values come from the backend's real-method preview schedule.
    const proposedIndex = buildProposedIndex(previewSchedule, effectiveDateStr);

    // Helper: turn a matched preview accrual row into a proposed calc-detail using
    // the contract's real method and the row's own _audit block.
    const proposedAccrualDetail = (pv) => {
      if (!pv) return null;
      const audit = pv._audit || {};
      return buildRealMethodCalcDetail(
        audit.principal != null ? audit.principal : pv.outstanding_balance,
        pv.interest_rate, pv.days_in_period,
        audit.day_count_basis != null ? audit.day_count_basis : dayCountBasis,
        pv.interest_calc_type || calcType,
        audit.compounding_frequency != null ? audit.compounding_frequency : compoundingFrequency,
        parseFloat(pv.gross_interest_amount != null ? pv.gross_interest_amount : pv.interest_amount) || 0,
        { mode: 'proposed', auditDailyRate: audit.compound_linear_daily_rate }
      );
    };

    // Track whether we've already handled the split month
    let splitHandled = false;

    enrichedSchedule.forEach(entry => {
      const entryDate = String(entry.payment_date || '').substring(0, 10);
      const isAfterEffective = effectiveDateStr && entryDate >= effectiveDateStr;
      const narration = entry.narration || '';
      const isInterest = isInterestNarration(narration);

      if (!isInterest) {
        // Non-interest entry: show as-is
        const curInt = parseFloat(entry.interest_amount || entry.net_interest_amount || 0);
        const capAmt = parseFloat(entry.capital_amount || 0);
        rows.push({
          payment_date: entry.payment_date,
          narration: narration,
          current_rate: entry.interest_rate,
          proposed_rate: null,
          days_in_period: null,
          current_interest: entry.interest_amount || entry.net_interest_amount,
          proposed_interest: null,
          capital_amount: entry.capital_amount,
          current_total: (curInt + capAmt) || null,
          proposed_total: null,
          current_balance: entry.outstanding_balance != null ? parseFloat(entry.outstanding_balance) : null,
          proposed_balance: entry.outstanding_balance != null ? parseFloat(entry.outstanding_balance) : null,
          is_impacted: false,
          flow_type: getFlowType(narration),
        });
      } else if (!isAfterEffective && isPeriodAccrual(narration)) {
        // Pre-effective accrual: unchanged by the rate change (proposed = null).
        const curInt = parseFloat(entry.interest_amount || entry.net_interest_amount || 0);
        const capAmt = parseFloat(entry.capital_amount || 0);
        rows.push({
          payment_date: entry.payment_date,
          narration: narration,
          current_rate: entry.interest_rate,
          proposed_rate: null,
          days_in_period: entry.days_in_period,
          current_interest: entry.interest_amount || entry.net_interest_amount,
          proposed_interest: null,
          capital_amount: entry.capital_amount,
          current_total: (curInt + capAmt) || null,
          proposed_total: null,
          current_balance: entry.outstanding_balance != null ? parseFloat(entry.outstanding_balance) : null,
          proposed_balance: entry.outstanding_balance != null ? parseFloat(entry.outstanding_balance) : null,
          is_impacted: false,
          flow_type: getFlowType(narration),
          current_calc_detail: buildRealMethodCalcDetail(
            entry.outstanding_balance || entry.capital_amount,
            entry.interest_rate, entry.days_in_period, dayCountBasis,
            calcType, compoundingFrequency, curInt
          ),
        });
      } else if (!isAfterEffective && isInterestPaymentNarration(narration)) {
        // Pre-effective payment (regular, adhoc, or combined): unchanged.
        const curInt = parseFloat(entry.interest_amount || entry.net_interest_amount || 0);
        const capAmt = parseFloat(entry.capital_amount || 0);
        rows.push({
          payment_date: entry.payment_date,
          narration: narration,
          current_rate: entry.interest_rate,
          proposed_rate: null,
          days_in_period: null,
          current_interest: entry.interest_amount || entry.net_interest_amount,
          proposed_interest: null,
          capital_amount: entry.capital_amount,
          current_total: (curInt + capAmt) || null,
          proposed_total: null,
          current_balance: entry.outstanding_balance != null ? parseFloat(entry.outstanding_balance) : null,
          proposed_balance: entry.outstanding_balance != null ? parseFloat(entry.outstanding_balance) : null,
          is_impacted: false,
          flow_type: getFlowType(narration),
        });
      } else if (isPeriodAccrual(narration)) {
        // Post-effective accrual: check if this entry spans the effective date (mid-month split)
        const days = parseInt(entry.days_in_period) || 0;
        const principal = parseFloat(entry.outstanding_balance || entry.capital_amount || 0);
        const entryDateObj = new Date(entryDate + 'T00:00:00Z');

        // Calculate period start: payment_date - days_in_period
        const periodStartObj = new Date(entryDateObj);
        periodStartObj.setUTCDate(periodStartObj.getUTCDate() - days);
        const periodStartStr = periodStartObj.toISOString().substring(0, 10);

        // Check if this accrual spans the effective date (period starts before, ends at/after)
        const spansEffective = !splitHandled
          && effectiveDateObj
          && periodStartStr < effectiveDateStr
          && entryDate >= effectiveDateStr;

        if (spansEffective && days > 0) {
          // Split into two rows: pre-change and post-change
          splitHandled = true;

          // Calculate days for each portion
          const msPerDay = 86400000;
          const preChangeDays = Math.round((effectiveDateObj - periodStartObj) / msPerDay);
          const postChangeDays = days - preChangeDays;

          // Use the entry's own rate (not the rate change's old_rate) for historical accuracy
          const entryRateNum = parseFloat(entry.interest_rate || 0);

          if (preChangeDays > 0) {
            // Row 1: Pre-change portion (entry's own rate, unchanged by the rate
            // change). The proposed side equals the current side for this portion.
            const preGross = principal * (entryRateNum / 100) * (preChangeDays / dayCountBasis);

            rows.push({
              payment_date: effectiveDateStr,
              narration: 'Interest Accrual (Pre-Rate Change)',
              current_rate: entry.interest_rate,
              proposed_rate: entryRateNum,
              days_in_period: preChangeDays,
              current_interest: preGross,
              proposed_interest: preGross, // same - unchanged
              capital_amount: 0,
              current_total: preGross,
              proposed_total: preGross,
              current_balance: principal,
              proposed_balance: principal,
              is_impacted: false,
              flow_type: 'accrual',
              is_split: true,
              current_calc_detail: buildRealMethodCalcDetail(
                principal, entryRateNum, preChangeDays, dayCountBasis,
                calcType, compoundingFrequency, preGross, { mode: 'current' }
              ),
              calc_detail: buildRealMethodCalcDetail(
                principal, entryRateNum, preChangeDays, dayCountBasis,
                calcType, compoundingFrequency, preGross, { mode: 'proposed' }
              ),
            });
          }

          if (postChangeDays > 0) {
            // Row 2: Post-change portion. The CURRENT side is a pro-rata
            // reconstruction at the entry's booked rate; the PROPOSED side is read
            // from the backend preview schedule (real calc method at the new rate).
            const currentPostGross = principal * (entryRateNum / 100) * (postChangeDays / dayCountBasis);
            const pv = takeProposed(proposedIndex, entryDate, 'accrual');
            const proposedPostGross = pv != null
              ? parseFloat(pv.gross_interest_amount != null ? pv.gross_interest_amount : pv.interest_amount) || 0
              : principal * (newRateNum / 100) * (postChangeDays / dayCountBasis);

            const isImpacted = Math.abs(currentPostGross - proposedPostGross) > 0.01;

            rows.push({
              payment_date: entry.payment_date,
              narration: 'Interest Accrual',
              current_rate: entry.interest_rate,
              proposed_rate: pv != null && pv.interest_rate != null ? parseFloat(pv.interest_rate) : newRateNum,
              days_in_period: pv != null && pv.days_in_period != null ? pv.days_in_period : postChangeDays,
              current_interest: currentPostGross,
              proposed_interest: proposedPostGross,
              capital_amount: 0,
              current_total: currentPostGross,
              proposed_total: proposedPostGross,
              current_balance: principal,
              proposed_balance: pv != null && pv.outstanding_balance != null ? parseFloat(pv.outstanding_balance) : principal,
              is_impacted: isImpacted,
              flow_type: 'accrual',
              is_split: true,
              current_calc_detail: buildRealMethodCalcDetail(
                principal, entryRateNum, postChangeDays, dayCountBasis,
                calcType, compoundingFrequency, currentPostGross, { mode: 'current' }
              ),
              calc_detail: pv != null
                ? proposedAccrualDetail(pv)
                : buildRealMethodCalcDetail(principal, newRateNum, postChangeDays, dayCountBasis, calcType, compoundingFrequency, proposedPostGross, { mode: 'proposed' }),
            });
          }
        } else {
          // Normal post-effective accrual: proposed value read from the backend
          // preview schedule (real calc method), not re-derived flat.
          const pv = takeProposed(proposedIndex, entryDate, 'accrual');
          const proposedGross = pv != null
            ? parseFloat(pv.gross_interest_amount != null ? pv.gross_interest_amount : pv.interest_amount) || 0
            : principal * (newRateNum / 100) * (days / dayCountBasis);

          const currentInterest = parseFloat(entry.interest_amount || entry.net_interest_amount || 0);
          const isImpacted = Math.abs(currentInterest - proposedGross) > 0.01;

          const capAmt = parseFloat(entry.capital_amount || 0);
          rows.push({
            payment_date: entry.payment_date,
            narration: narration,
            current_rate: entry.interest_rate,
            proposed_rate: pv != null && pv.interest_rate != null ? parseFloat(pv.interest_rate) : newRateNum,
            days_in_period: pv != null && pv.days_in_period != null ? pv.days_in_period : entry.days_in_period,
            current_interest: entry.interest_amount || entry.net_interest_amount,
            proposed_interest: proposedGross,
            capital_amount: entry.capital_amount,
            current_total: (currentInterest + capAmt) || null,
            proposed_total: (proposedGross + capAmt) || null,
            current_balance: entry.outstanding_balance != null ? parseFloat(entry.outstanding_balance) : principal,
            proposed_balance: pv != null && pv.outstanding_balance != null ? parseFloat(pv.outstanding_balance) : (entry.outstanding_balance != null ? parseFloat(entry.outstanding_balance) : principal),
            is_impacted: isImpacted,
            flow_type: getFlowType(narration),
            current_calc_detail: buildRealMethodCalcDetail(
              principal, entry.interest_rate, days, dayCountBasis,
              calcType, compoundingFrequency, currentInterest, { mode: 'current' }
            ),
            calc_detail: pv != null
              ? proposedAccrualDetail(pv)
              : buildRealMethodCalcDetail(principal, newRateNum, days, dayCountBasis, calcType, compoundingFrequency, proposedGross, { mode: 'proposed' }),
          });
        }
      } else if (isInterestPaymentNarration(narration)) {
        // Post-effective payment (regular, adhoc, or combined): proposed net is
        // read from the matching backend preview payment row (already WHT-adjusted
        // by the real-method generation), not re-accumulated client-side.
        const pv = takeProposed(proposedIndex, entryDate, 'payment');
        const currentInterest = parseFloat(entry.interest_amount || entry.net_interest_amount || 0);
        const proposedNet = pv != null
          ? parseFloat(pv.net_interest_amount != null ? pv.net_interest_amount : (pv.payment_amount != null ? pv.payment_amount : pv.interest_amount)) || 0
          : currentInterest;
        const isImpacted = Math.abs(currentInterest - proposedNet) > 0.01;

        const capAmt = parseFloat(entry.capital_amount || 0);
        rows.push({
          payment_date: entry.payment_date,
          narration: narration,
          current_rate: entry.interest_rate,
          proposed_rate: pv != null && pv.interest_rate != null ? parseFloat(pv.interest_rate) : newRateNum,
          days_in_period: null,
          current_interest: entry.interest_amount || entry.net_interest_amount,
          proposed_interest: proposedNet,
          capital_amount: entry.capital_amount,
          current_total: (currentInterest + capAmt) || null,
          proposed_total: (proposedNet + capAmt) || null,
          current_balance: entry.outstanding_balance != null ? parseFloat(entry.outstanding_balance) : null,
          proposed_balance: pv != null && pv.outstanding_balance != null ? parseFloat(pv.outstanding_balance) : (entry.outstanding_balance != null ? parseFloat(entry.outstanding_balance) : null),
          is_impacted: isImpacted,
          flow_type: getFlowType(narration),
        });
      }
    });

    // A rate change does not alter capital, so the Current/Proposed Capital
    // columns are both the row's own capital amount (the shared table renders a
    // paired Capital column for consistency with capital-amendment previews).
    rows.forEach((r) => {
      const cap = r.capital_amount != null ? parseFloat(r.capital_amount) : null;
      if (r.current_capital === undefined) r.current_capital = cap;
      if (r.proposed_capital === undefined) r.proposed_capital = r.proposed_rate != null || r.proposed_interest != null ? cap : null;
    });

    return rows;
  };

  const rateChange = previewData?.rate_change;
  const comparison = previewData?.comparison;
  const isIncrease = rateChange && rateChange.new_rate > rateChange.old_rate;

  // buildDisplayRows is pure over these inputs; memoize on the data, not fn identity.
  const comparisonRows = useMemo(
    () => (previewData
      ? buildDisplayRows(
          previewData.current_schedule,
          rateChange?.effective_date,
          rateChange?.old_rate,
          rateChange?.new_rate,
          rateChange?.day_count_basis || 365,
          rateChange?.interest_calc_type || 'Simple',
          rateChange?.compounding_frequency || 12,
          previewData.preview_schedule || [],
        )
      : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- buildDisplayRows defined in render; deps are its data inputs
    [previewData, rateChange?.effective_date, rateChange?.old_rate, rateChange?.new_rate,
      rateChange?.day_count_basis, rateChange?.interest_calc_type, rateChange?.compounding_frequency],
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Rate Change Impact Preview
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      {refreshing && <LinearProgress />}
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && previewData && (
          <>
            {/* Rate Change Summary */}
            <Alert
              severity="info"
              icon={isIncrease ? <TrendingUpIcon /> : <TrendingDownIcon />}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2">
                <strong>Rate Change:</strong> {rateChange.old_rate.toFixed(4)}% → {rateChange.new_rate.toFixed(4)}%
                {' '}(effective {formatDate(rateChange.effective_date)})
                <br />
                <strong>Reason:</strong> {rateChange.reason || 'Not specified'}
              </Typography>
            </Alert>

            {/* Backdated change touches journal-exported periods: warn + require ack */}
            {previewData?.affects_exported_periods && (
              <Alert
                severity="warning"
                icon={<WarningIcon />}
                sx={{ mb: 2 }}
              >
                <AlertTitle>This change affects journal-exported periods</AlertTitle>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {previewData.exported_periods.length} accrual period{previewData.exported_periods.length === 1 ? '' : 's'} {previewData.exported_periods.length === 1 ? 'has' : 'have'} already been journal-exported.
                  The original entries will be preserved. On approval, an <strong>Interest Reversal</strong> + <strong>Interest Re-accrual</strong>
                  {' '}pair will be generated for each so the GL is corrected on the next export.
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Period</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Original</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Corrected</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Net adjustment</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Matched payment</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewData.exported_periods.map((p) => (
                        <TableRow key={p.schedule_id}>
                          <TableCell>{formatDate(p.payment_date)}</TableCell>
                          <TableCell align="right">{formatCurrency(p.original_amount)}</TableCell>
                          <TableCell align="right">
                            {p.new_amount == null
                              ? <em>unmatched in preview</em>
                              : formatCurrency(p.new_amount)}
                          </TableCell>
                          <TableCell align="right">
                            {p.net_adjustment == null
                              ? '-'
                              : formatDifference(p.net_adjustment)}
                          </TableCell>
                          <TableCell>
                            {p.has_matched_payment
                              ? <Chip size="small" color="warning" label="Matched" />
                              : <Chip size="small" label="Unmatched" />}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Alert>
            )}

            {/* Subset that ALSO has a matched payment -- extra callout */}
            {previewData?.exported_periods_matched?.length > 0 && (
              <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                <AlertTitle>
                  {previewData.exported_periods_matched.length} of those period{previewData.exported_periods_matched.length === 1 ? '' : 's'}
                  {' '}also {previewData.exported_periods_matched.length === 1 ? 'has' : 'have'} a matched payment
                </AlertTitle>
                <Typography variant="body2">
                  Generating reversal entries will <strong>not</strong> unmatch the payment(s).
                  You may need to re-evaluate the match against the corrected accrual after the next journal export.
                </Typography>
              </Alert>
            )}

            {/* Acknowledgement gate */}
            {previewData?.affects_exported_periods && showApprove && (
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ackReversal}
                      onChange={(e) => setAckReversal(e.target.checked)}
                    />
                  }
                  label="I understand — generate reversal + re-accrual entries for the affected exported periods."
                />
              </Box>
            )}

            {/* Impact Summary */}
            {comparison && (
              <ImpactSummaryCards
                currentInterest={comparison.current.total_interest}
                proposedInterest={comparison.preview.total_interest}
                difference={comparison.difference.total_interest}
                percentageChange={comparison.difference.percentage_change}
                currentWht={comparison.current.total_wht}
                proposedWht={comparison.preview.total_wht}
                currencyCode={currencyCode}
                dp={dp}
              />
            )}

            {/* Unified Comparison Table (shared across all impact previews) */}
            <ScheduleComparisonTable
              rows={comparisonRows}
              isIncrease={isIncrease}
              dp={dp}
              currentLabel="Current"
              proposedLabel="Proposed"
              changeTypeLabel="Rate Change"
              currentVersion={previewData?.current_version ?? null}
              nextVersion={previewData?.next_version ?? null}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={approving}>
          Close
        </Button>
        {showApprove && (
          <Button
            variant="contained"
            color="success"
            startIcon={approving ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            onClick={handleApprove}
            disabled={
              loading
              || approving
              || !previewData
              || (previewData?.affects_exported_periods && !ackReversal)
            }
          >
            {approving
              ? 'Approving...'
              : previewData?.affects_exported_periods
                ? 'Confirm — generate reversal entries'
                : 'Approve Rate Change'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default RateChangePreviewModal;
